/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2019, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import {
  Analyze,
  ChartLine,
  Ideate,
  MobileChat
} from '@carbon/pictograms-react';
import { Button, Link, Modal, Tile } from 'carbon-components-react';
import React from 'react';

import { schedulerProp } from '@ibm-watson/shared/modules/ia/util/contactModule';
import { ctaEvents } from '@ibm-watson/shared/modules/ia/util/trackers/segment';
import { Noop } from '../../../../ts';

const strings = {
  WELCOME_HEADING: 'Welcome to Trial!',
  WELCOME_TEXT1:
    'Enjoy exploring our advanced feature set for the next 30 days.',
  WELCOME_TEXT2: "Here's our top features to get you started:",
  WELCOME_CHAT:
    'With Trial, you also unlocked access to chat with a live person about your assistant.',
  BOOK_APPOINTMENT: 'Book a consultation',
  TILE1_HEADING: 'Train faster',
  TILE1_TEXT:
    'Use intent recommendations to automatically cluster common questions your users are asking.',
  TILE2_HEADING: 'Get better answers',
  TILE2_TEXT:
    "Answer user's less common questions immediately with Search Skill which can crawl your support site (or any other content for that matter).",
  TILE3_HEADING: 'Get 23 more days of data',
  TILE3_TEXT:
    'Our easy-to-use analytics dashboard allows you to visualize 30 days of trends in your user message data and take action to improve when required.',
  TILE4_HEADING: 'Chat with more users',
  TILE4_TEXT:
    'Now you can chat with 4,000 additional users for free during this trial period.',
  LEARN_MORE: 'Learn more'
};

const tiles = [
  {
    icon: Ideate,
    head: 'TILE1_HEADING',
    text: 'TILE1_TEXT',
    learnMore: 'https://www.youtube.com/watch?v=bBTSnsD8yLA'
  },
  {
    icon: Analyze,
    head: 'TILE2_HEADING',
    text: 'TILE2_TEXT',
    learnMore: 'https://www.youtube.com/watch?v=_UgRPaxipgI'
  },
  {
    icon: ChartLine,
    head: 'TILE3_HEADING',
    text: 'TILE3_TEXT',
    learnMore:
      'https://cloud.ibm.com/docs/assistant?topic=assistant-logs-overview'
  },
  {
    icon: MobileChat,
    head: 'TILE4_HEADING',
    text: 'TILE4_TEXT'
  }
];

const scheduleClick = () => {
  ctaEvents.bookConsultation({ category: 'Trial Welcome Modal' });
};

export interface TrialWelcomeModalProps {
  onClose: Noop;
  hasScheduler: boolean;
}

const TrialWelcomeModal: React.FC<TrialWelcomeModalProps> = ({
  onClose,
  hasScheduler
}) => (
  <Modal
    className="TrialWelcomeModal"
    open={true}
    passiveModal={true}
    modalHeading={strings.WELCOME_HEADING}
    onRequestClose={onClose}>
    <p>{strings.WELCOME_TEXT1}</p>
    <p>{strings.WELCOME_TEXT2}</p>
    <div className="TrialWelcomeModal__tiles">{tiles.map(renderTile)}</div>
    {hasScheduler && (
      <div className="TrialWelcomeModal__chat">
        <span>{strings.WELCOME_CHAT}</span>
        <Button
          kind="primary"
          size="sm"
          onClick={scheduleClick}
          data-modal-primary-focus
          {...schedulerProp}>
          {strings.BOOK_APPOINTMENT}
        </Button>
      </div>
    )}
  </Modal>
);

const renderTile = (tile, index) => (
  <Tile key={`tile${index}`} className="TrialWelcomeModal__tile" light={true}>
    {React.createElement(tile.icon, { width: 64, height: 64 })}
    <h4 className="productive-heading-01">{strings[tile.head]}</h4>
    <p className="body-short-01">
      {strings[tile.text]}
      {tile.learnMore && (
        <Link
          className="TrialWelcomeModal__link"
          href={tile.learnMore}
          target="_blank"
          rel="noopener noreferrer">
          {strings.LEARN_MORE}
        </Link>
      )}
    </p>
  </Tile>
);

export default TrialWelcomeModal;
